import React from 'react';

import { studioTheme, hexToRgba } from './studioTheme';
import { StudioWrap, Avatar, Identity, SourceLogo, Stars, DateText } from './StudioCardKit';
import { StudioBody, isMediaTestimonial, StudioMediaCard } from './StudioBody';

/**
 * Aurora Glass (widget type 10)
 *
 * A glass panel with a soft aurora bloom behind the header and a bright
 * hairline along the top edge. Light by default; the wall's dark mode toggle
 * swaps it to the deep version.
 */
const AuroraGlassCard = (props) => {
  const { cardItem, socialCardType } = props;
  const t = studioTheme(props);

  const bloom = hexToRgba(t.brand, t.dark ? 0.3 : 0.22);

  const surface = t.dark
    ? 'linear-gradient(180deg,#191d28,#0f1117)'
    : 'linear-gradient(180deg,#ffffff,#f7f7fd)';

  /* One body type scale per card: the text testimonial and the video/audio
     summary must read as the same widget. */
  const bodyTextStyle = { fontSize: t.smallSize ? 14 : 14.5, lineHeight: 1.55 };

  /* A video or audio testimonial IS the card - the player already carries
     the name and work title, so none of the chrome below is drawn. Only the
     summary keeps this widget's look, as the panel it extends into. */
  if (isMediaTestimonial(cardItem, socialCardType)) {
    return (
      <StudioMediaCard
        cardItem={cardItem}
        socialCardType={socialCardType}
        t={t}
        enclosure={{
          border: `1px solid ${t.dark ? 'rgba(255,255,255,0.12)' : 'rgba(20,24,40,0.07)'}`,
          borderRadius: 16,
          background: surface,
          /* the wall's custom card colour wins, as it does on the text card */
          ...(t.cardBackgroundObject || {}),
          boxShadow: t.dark
            ? 'inset 0 1px 0 rgba(255,255,255,0.08), 0 14px 34px rgba(0,0,0,0.45)'
            : 'inset 0 1px 0 rgba(255,255,255,0.9), 0 12px 30px rgba(20,24,40,0.10)',
        }}
        summaryStyle={bodyTextStyle}
      />
    );
  }

  return (
    <StudioWrap>
      <div
        style={{
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 16,
          padding: 18,
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
          border: `1px solid ${t.dark ? 'rgba(255,255,255,0.12)' : 'rgba(20,24,40,0.07)'}`,
          background: surface,
          ...(t.cardBackgroundObject || {}),
          boxShadow: t.dark
            ? 'inset 0 1px 0 rgba(255,255,255,0.08), 0 14px 34px rgba(0,0,0,0.45)'
            : 'inset 0 1px 0 rgba(255,255,255,0.9), 0 12px 30px rgba(20,24,40,0.10), 0 1px 2px rgba(20,24,40,0.05)',
        }}
      >
        {/* aurora bloom */}
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            left: '-20%',
            right: '-20%',
            top: '-40%',
            height: '70%',
            background: `radial-gradient(50% 50% at 50% 30%, ${bloom}, rgba(0,0,0,0) 70%)`,
            pointerEvents: 'none',
          }}
        />

        <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            <Avatar cardItem={cardItem} size={40} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <Identity t={t} cardItem={cardItem} />
            </div>
            <SourceLogo cardItem={cardItem} />
          </div>

          <StudioBody
            t={t}
            cardItem={cardItem}
            socialCardType={socialCardType}
            textStyle={bodyTextStyle}
          />

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 10,
              flexWrap: 'wrap',
              paddingTop: 2,
            }}
          >
            <Stars t={t} cardItem={cardItem} size={13} />
            <DateText t={t} cardItem={cardItem} />
          </div>
        </div>
      </div>
    </StudioWrap>
  );
};

export default AuroraGlassCard;
